import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import * as api from '../services/api';
import './AccountSettingsModal.css';

export default function AccountSettingsModal({ isOpen, onClose }) {
  const { user, loadUser } = useAuth();
  const [activeTab, setActiveTab] = useState('name'); // 'name', 'phone', 'theme'
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [otp, setOtp] = useState('');
  const [otpSent, setOtpSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    if (isOpen && user) {
      setName(user.name || '');
      setPhone(user.phone || '');
      setOtpSent(false);
      setOtp('');
      setError('');
      setSuccess('');
    }
  }, [isOpen, user]);

  const handleUpdateName = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      await api.updateUserProfile({ name });
      await loadUser();
      setSuccess('Name updated successfully!');
      setTimeout(() => {
        onClose();
      }, 1500);
    } catch (error) {
      setError(error.response?.data?.message || 'Failed to update name');
    } finally {
      setLoading(false);
    }
  };

  const handleSendOTP = async () => {
    if (!phone || phone.length !== 10) {
      setError('Please enter a valid 10-digit phone number');
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const result = await api.sendPhoneOTP(phone);
      setOtpSent(true);
      setSuccess('OTP sent successfully!');
      // In development, show OTP in console for testing
      if (result.otp) {
        console.log('OTP (dev only):', result.otp);
      }
    } catch (error) {
      setError(error.response?.data?.message || 'Failed to send OTP');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOTP = async (e) => {
    e.preventDefault();
    if (!otp || otp.length !== 6) {
      setError('Please enter a valid 6-digit OTP');
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      // First verify the OTP
      const verifyResult = await api.verifyPhoneOTP(phone, otp);
      if (!verifyResult.verified) {
        setError('Invalid OTP. Please try again.');
        return;
      }

      // If OTP is verified, update the phone number
      await api.updateUserProfile({ phone });
      await loadUser();
      setSuccess('Phone number updated successfully!');
      setTimeout(() => {
        onClose();
      }, 1500);
    } catch (error) {
      setError(error.response?.data?.message || 'Failed to verify OTP');
    } finally {
      setLoading(false);
    }
  };

  const handleThemeChange = (theme) => {
    document.documentElement.setAttribute('data-theme', theme);
    localStorage.setItem('theme', theme);
    setSuccess('Theme updated successfully!');
    setTimeout(() => {
      onClose();
      window.location.reload();
    }, 1000);
  };

  if (!isOpen) return null;

  return (
    <div className="account-settings-overlay" onClick={onClose}>
      <div className="account-settings-modal" onClick={(e) => e.stopPropagation()}>
        <button className="modal-close" onClick={onClose}>×</button>
        
        <div className="modal-header">
          <h2>Manage Account</h2>
        </div>

        <div className="modal-tabs">
          <button
            className={`tab ${activeTab === 'name' ? 'active' : ''}`}
            onClick={() => setActiveTab('name')}
          >
            Change Name
          </button>
          <button
            className={`tab ${activeTab === 'phone' ? 'active' : ''}`}
            onClick={() => setActiveTab('phone')}
          >
            Change Phone
          </button>
          <button
            className={`tab ${activeTab === 'theme' ? 'active' : ''}`}
            onClick={() => setActiveTab('theme')}
          >
            Theme
          </button>
        </div>

        <div className="modal-content">
          {activeTab === 'name' && (
            <form onSubmit={handleUpdateName} className="settings-form">
              <div className="form-group">
                <label>Full Name</label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Enter your full name"
                  required
                />
              </div>
              {error && <div className="error-message">{error}</div>}
              {success && <div className="success-message">{success}</div>}
              <button type="submit" className="btn primary" disabled={loading}>
                {loading ? 'Updating...' : 'Update Name'}
              </button>
            </form>
          )}

          {activeTab === 'phone' && (
            <form onSubmit={otpSent ? handleVerifyOTP : handleSendOTP} className="settings-form">
              <div className="form-group">
                <label>Phone Number</label>
                <input
                  type="tel"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="Enter your phone number"
                  required
                  disabled={otpSent}
                  pattern="[0-9]{10}"
                  maxLength="10"
                />
                <small>Enter 10-digit phone number</small>
              </div>

              {otpSent && (
                <div className="form-group">
                  <label>Enter OTP</label>
                  <input
                    type="text"
                    value={otp}
                    onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    placeholder="Enter 6-digit OTP"
                    required
                    pattern="[0-9]{6}"
                    maxLength="6"
                  />
                  <div className="otp-actions">
                    <button
                      type="button"
                      className="btn-link"
                      onClick={handleSendOTP}
                      disabled={loading}
                    >
                      Resend OTP
                    </button>
                  </div>
                </div>
              )}

              {error && <div className="error-message">{error}</div>}
              {success && <div className="success-message">{success}</div>}
              
              <button type="submit" className="btn primary" disabled={loading}>
                {loading 
                  ? (otpSent ? 'Verifying...' : 'Sending...')
                  : (otpSent ? 'Verify OTP' : 'Send OTP')
                }
              </button>
            </form>
          )}

          {activeTab === 'theme' && (
            <div className="theme-settings">
              <h3>Choose Theme</h3>
              <div className="theme-options">
                <button
                  className={`theme-option ${localStorage.getItem('theme') === 'light' ? 'active' : ''}`}
                  onClick={() => handleThemeChange('light')}
                >
                  <div className="theme-preview light">
                    <div className="preview-header"></div>
                    <div className="preview-content"></div>
                  </div>
                  <span>Light</span>
                </button>
                <button
                  className={`theme-option ${localStorage.getItem('theme') === 'dark' ? 'active' : ''}`}
                  onClick={() => handleThemeChange('dark')}
                >
                  <div className="theme-preview dark">
                    <div className="preview-header"></div>
                    <div className="preview-content"></div>
                  </div>
                  <span>Dark</span>
                </button>
              </div>
              {success && <div className="success-message">{success}</div>}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

